import React, { useContext } from 'react';
import PropTypes from 'prop-types';
// eslint-disable-next-line import/no-extraneous-dependencies
import _ from 'lodash';
import { GradeContext } from "../index";
import Alert from '../../../components/Alert';
import trackEvent from '../eventTracking';
import { avatarPath } from '../../../styleguide/Avatar';
import CopyToClipboard from '../../../components/CopyToClipboard';

function StudentList(props) {
  const {
    yourX,
    players,
    lengthOfPlayers,
    maxPlayers,
    selectedChildren,
    lengthOfSelected,
    addStudentRedirectError,
    onPrintCodesClick,
    openEditChildModal,
    openDeleteChildModal,
    openBulkEditChildModal,
    openBulkDeleteChildModal,
    toggleSelected,
    toggleLockHideGrade,
    toggleSelectHeader
  } = props;

  const [maxPlayersErrorMessage] =
    React.useState(
      `You're using all available ${yourX.plural} accounts. 
      Contact support@education.com to add more ${yourX.plural}.`);

  const [error, setError] =
    React.useState(addStudentRedirectError ? maxPlayersErrorMessage : null);
  const [buttonsAreSticking, setButtonsAreSticking] = React.useState(false);
  const [sortBy, setSortBy] = React.useState(null);
  const [sortAsc, setSortAsc] = React.useState(true);

  const buttonsDiv = React.useRef(null);
  const wholeDiv = React.useRef(null)

  const codeSpans = React.useRef({ });

  function checkButtonPosition() {
    // buttonDivInfo.button is the # of pixels from top of the page to the bottom oof the buttons div
    // window.innerHeight is the # of visible pixels for the height of the browser. this changes if browser is resized
    // As soon as the bottom of the buttons div becomes invisible, turn on the sticky. otherwise, off.
    const buttonDivInfo = buttonsDiv.current.getBoundingClientRect();
    const wholeDivInfo = wholeDiv.current.getBoundingClientRect();

    setButtonsAreSticking(buttonDivInfo.height > 0 && buttonDivInfo.top <= 14 && wholeDivInfo.bottom > 75);
  }

  function handleScroll() {
    checkButtonPosition();
  }

  React.useEffect(()=> {
    window.addEventListener('scroll', handleScroll);
    checkButtonPosition();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    }
  }, []);

  /**
   * Updates the state of the sortBy and sortAsc values, based on the clicked column.
   * @param colName
   */
  function onClickSort(colName) {
    if (colName === sortBy) {
      setSortAsc(!sortAsc);
    } else {
      setSortBy(colName);
      setSortAsc(true);
    }
  }

  function openEditChild(e, playerId) {
    e.stopPropagation();
    openEditChildModal(playerId);
  }

  function onStudentCodeClick(e, playerId) {
    e.stopPropagation();
    const element = codeSpans.current[playerId];
    window.getSelection().selectAllChildren(
      element
    );
  }

  function onDismissError() {
    setError(null);
  }

  function openDeleteChild(e, playerId) {
    e.stopPropagation();
    trackEvent('delete', 'started', false);
    openDeleteChildModal(playerId);
  }

  function onAddChildClick() {
    // Only allow add student flow if they haven't already reached maxPlayers
    if (lengthOfPlayers >= maxPlayers) {
      setError(maxPlayersErrorMessage);
    } else {
      trackEvent('add', 'started', true);
      window.location.href= "/my/students/add/";
    }
  }

  /**
   * Function to be used in the sort function to compare two object's key values (i.e. to compare a[key] and b[key]).
   * @param key
   * @param order - 'asc' or 'desc'
   * @returns {(function(*, *): (number|number))|*}
   */
  function compareKeyValues(key, order = 'desc') {
    return function innerSort(a, b) {
      if (!(key in a) || !(key in b)) {
        // property doesn't exist on either object
        return 0;
      }

      const varA = (typeof a[key] === 'string') ? a[key].toUpperCase() : a[key];
      const varB = (typeof b[key] === 'string') ? b[key].toUpperCase() : b[key];

      let comparison = 0;

      if (varA > varB) {
        comparison = 1;
      } else if (varA < varB) {
        comparison = -1;
      }

      return (
        (order === 'desc') ? (comparison * -1) : comparison
      );
    };
  }

  /**
   * Returns a sorted array of childIds. It will not be sorted if this.state.sortBy is not set.
   * @returns {string[]|*[]}
   */
  function getSortedPlayers() {
    if (!sortBy) {
      return Object.keys(players);
    }

    // Add the players to an array we can sort.
    const sortable = [];
    Object.keys(players).forEach(
      (player) => { sortable.push(players[player]); })

    // Sort the players and return an array of their sorted childIds.
    const sortedPlayers =
      sortable.sort(compareKeyValues(sortBy, sortAsc ? 'asc' : 'desc'));
    return sortedPlayers.map(player => player.childId);
  }

  function getStudent(playerId) {
    // players is a dict with playerId as the key, and another dict as the value
    if (players[playerId]) {
      return players[playerId];
    }

    return null;
  }

  /**
   * Returns the sort icon for the given column, based on the current sortBy and sortAsc state values.
   * @param colName
   * @returns {JSX.Element}
   */
  function sortIconDesktop(colName) {
    // If the current sortBy is not this column, return the default "double-arrow" icon
    if (colName !== sortBy) {
      return <i className="icon-sort"/>
    }

    // Display the icon related to the current sort order
    if (sortAsc) {
      return <i className="icon-sort-down sort-now"/>
    }

    return <i className="icon-sort-up sort-now"/>
  }

  function togSelected(e, playerId) {
    e.stopPropagation();  // Skip entire row's onClick event\
    toggleSelected(playerId);
  }

  function togLockHideGrade(e, playerId, lockOrHide='lock') {
    e.stopPropagation();  // Skip entire row's onClick event
    trackEvent('toggle', 'started and completed', false, lockOrHide);
    toggleLockHideGrade(playerId,  lockOrHide);
  }

  function getRowPerStudent(playerId) {
    const student = getStudent(playerId);

    if (!student) {
      return '';
    }

    const isChecked = Object.keys(selectedChildren).includes(playerId);
    const childAvatar = avatarPath(student.avatar.internalName);

    const iconLockGrade = <div className="img-container" role="button" tabIndex={0}
                               onClick={(e) => togLockHideGrade(e, playerId,  'lock')}
                               onKeyDown={(e) => togLockHideGrade(e, playerId,  'lock')}>
      <img src="/files/static/my-students/locked.png" alt='locked'/></div>;
    const iconNoLockGrade = <div className="img-container" role="button" tabIndex={0}
                                 onClick={(e) => togLockHideGrade(e, playerId,  'lock')}
                                 onKeyDown={(e) => togLockHideGrade(e, playerId,  'lock')}>
      <img src="/files/static/my-students/unlocked.png" alt='unlocked'/></div>;
    const iconHideGrade = <div className="img-container" role="button" tabIndex={0}
                               onClick={(e) => togLockHideGrade(e, playerId,  'hide')}
                               onKeyDown={(e) => togLockHideGrade(e, playerId,  'hide')}>
      <img src="/files/static/my-students/closed-eye.png" alt='hide'/></div>;
    const iconNoHideGrade = <div className="img-container" role="button" tabIndex={0}
                                 onClick={(e) => togLockHideGrade(e, playerId,  'hide')}
                                 onKeyDown={(e) => togLockHideGrade(e, playerId,  'hide')}>
      <img src="/files/static/my-students/open-eye.png" alt='unhide'/></div>;

    const { gradeMap } = useContext(GradeContext);
    let studentGrade = gradeMap !== null ? gradeMap[student.grade] : false;
    studentGrade = studentGrade ? studentGrade.longDisplay : "";

    return (
      <tr onClick={() => openEditChildModal(playerId)} key={playerId}>
        <td>
          <div
            className="checkbox"
            role="button"
            tabIndex={0}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={isChecked}
              onClick={(e) => togSelected(e, playerId)}
              onChange={() => {}}
            />
            <div className="checkbox" />
          </div>
        </td>
        <td className="student-name">
          <img className="student-avatar" src={childAvatar} alt="avatar" />
          <div>
            <span>{_.unescape(student.name)}</span>
            <br />
            <span className="student-name-grade">{studentGrade}</span>
          </div>
        </td>
        <td className="student-code">
          <div className="content-inline-horizontal">
            {/* eslint-disable-next-line no-return-assign */}
            <span ref={(element) => (codeSpans.current[playerId] = element)}
              role="button"
              tabIndex={0}
              onClick={(e) => onStudentCodeClick(e, playerId)}
              onKeyDown={(e) => onStudentCodeClick(e, playerId)}
            >
              {student.studentCode}
            </span>
            <CopyToClipboard
              copyText={student.studentCode}
              tooltipText="Copy student code"
            />
          </div>
        </td>
        <td>{studentGrade}</td>
        <td className="center-content">
          {student.lockedGrade ? iconLockGrade : iconNoLockGrade}
        </td>
        <td className="center-content">
          {student.hiddenGrade ? iconHideGrade : iconNoHideGrade}
        </td>
        <td>
          <div className="content-dist-horizontal">
            <div onClick={(e) => openEditChild(e, playerId)}
                 onKeyDown={(e) => openEditChild(e, playerId)} role='button' tabIndex={0}>
              <i className="icon-pencil clickable-icon" />
            </div>
            <div onClick={(e) => openEditChild(e, playerId)}
                 onKeyDown={(e) => openDeleteChild(e, playerId)} role='button' tabIndex={0}>
              <i className="icon-trash-light clickable-icon" />
            </div>
          </div>
        </td>
      </tr>
    );
  }

  const allIsChecked = (lengthOfPlayers > 0) && (lengthOfPlayers === lengthOfSelected);

  return (
    <div ref={wholeDiv} className='student-list'>
      {error && lengthOfPlayers >= maxPlayers &&
        <Alert color="error" dismissable onClose={onDismissError}>
          {error}
        </Alert>
      }

      {/* Non-Mobile Version */}
      <div className='bulk-action-buttons' data-cy="bulk-action-buttons">
        <button type="button" className="btn btn-primary btn-add-students" onClick={onAddChildClick}>
          <i className="icon-plus-light" />{`Add ${  yourX.singular}`}
        </button>
        <button type="button" className={`btn btn-secondary${  lengthOfSelected === 0 ? " disabled" : ""}`}
                onClick={openBulkEditChildModal}>
          <i className="icon-pencil" /> {lengthOfSelected > 0 ? `Edit (${  lengthOfSelected  })` : 'Edit'}
        </button>
        <button type="button" className={`btn btn-secondary${  lengthOfSelected === 0 ? " disabled" : ""}`}
                onClick={onPrintCodesClick}>
          <i className="icon-print" /> Print codes
        </button>
        <button type="button"
                className={`btn ${  lengthOfSelected === 0 ? "btn-secondary disabled" : "btn-destructive btn-outline"}`}
                onClick={openBulkDeleteChildModal}>
          <i className="icon-trash-light" /> {lengthOfSelected > 0 ? `Delete (${  lengthOfSelected  })` : 'Delete'}
        </button>
      </div>
      {/* End of Non-Mobile Version */}

      { /* Mobile Version */ }
      <div ref={buttonsDiv} className='bulk-action-buttons bulk-action-buttons-mobile' data-cy="bulk-action-buttons">
        <div className="bulk-action-button-container">
          <button type="button" className="btn btn-primary bulk-action-button" onClick={onAddChildClick}>
            <i className="icon-plus-light" />
          </button>
          <div className="bulk-action-button-text">Add</div>
        </div>

        <div className="bulk-action-button-container">
          <button type="button"
                  className={`btn btn-tertiary bulk-action-button${ lengthOfSelected === 0 ? " disabled" : ""}`}
                  onClick={openBulkEditChildModal}>
            <i className="icon-pencil" />
          </button>
          <div
            className={`bulk-action-button-text${  lengthOfSelected === 0 ? " disabled-button-text" : ""}`}>Edit
          </div>
        </div>

        <div className="bulk-action-button-container">
          <button type="button"
                  className={`btn btn-tertiary bulk-action-button${  lengthOfSelected === 0 ? " disabled" : ""}`}
                  onClick={onPrintCodesClick}>
            <i className="icon-print" />
          </button>
          <div
            className={`bulk-action-button-text${  lengthOfSelected === 0 ? " disabled-button-text" : ""}`}>Print
          </div>
        </div>

        <div className="bulk-action-button-container">
          <button type="button"
                  className={`btn btn-tertiary bulk-action-button${ lengthOfSelected === 0 ? " disabled" : ""}`}
                  onClick={openBulkDeleteChildModal}>
            <i className="icon-trash-light" />
          </button>
          <div
            className={`bulk-action-button-text${  lengthOfSelected === 0 ? " disabled-button-text" : ""}`}>Delete
          </div>
        </div>
      </div>
      { /* End of Mobile Version */ }

      { /* Mobile Version: Sticky */ }
      <div
        className={`bulk-action-buttons bulk-action-buttons-mobile sticky${ buttonsAreSticking ? " visible" : ""}`}
        data-cy="bulk-action-buttons">
        <div className="mobile-sticky-buttons-container">
          <div className="bulk-action-button-container">
            <button  type="button" className="btn btn-primary bulk-action-button" onClick={onAddChildClick}>
              <i className="icon-plus-light" />
            </button>
            <div className="bulk-action-button-text">Add</div>
          </div>

          <div className="bulk-action-button-container">
            <button  type="button"
                     className={`btn btn-tertiary bulk-action-button${ lengthOfSelected === 0 ? " disabled" : ""}`}
                    onClick={openBulkEditChildModal}>
              <i className="icon-pencil" />
            </button>
            <div
              className={`bulk-action-button-text${ lengthOfSelected === 0 ? " disabled-button-text" : ""}`}>Edit
            </div>
          </div>

          <div className="bulk-action-button-container">
            <button  type="button"
                     className={`btn btn-tertiary bulk-action-button${ lengthOfSelected === 0 ? " disabled" : ""}`}
                    onClick={onPrintCodesClick}>
              <i className="icon-print" />
            </button>
            <div className={`bulk-action-button-text${  lengthOfSelected === 0 ? " disabled-button-text" : ""}`}>Print
            </div>
          </div>

          <div className="bulk-action-button-container">
            <button type="button"
                     className={`btn btn-tertiary bulk-action-button${ lengthOfSelected === 0 ? " disabled" : ""}`}
                    onClick={openBulkDeleteChildModal}>
              <i className="icon-trash-light" />
            </button>
            <div className={`bulk-action-button-text${ lengthOfSelected === 0 ? " disabled-button-text" : ""}`}>Delete
            </div>
          </div>
        </div>
      </div>

      { /* End of Mobile Version: Sticky */ }

      <div className="form content">
        <table className="table students-table" data-cy="students-table">
          <thead>
          <tr>
            <th>
              <div className="checkbox">
                <input type="checkbox" checked={allIsChecked} onChange={() => toggleSelectHeader()} />
                <div className="checkbox" />
              </div>
            </th>

            { /* Sortable Student Name column */ }
            <th key="name" className="align-left">
              <span role="columnheader" tabIndex={0} className="col-head-text" onClick={() => {onClickSort("name")}}
                    onKeyDown={() => {onClickSort("name")}}>
                {yourX.plural}
                <span className="sort-icon-btn"> {sortIconDesktop("name")} </span>
              </span>
            </th>
            <th className="align-left">Student Code</th>
            <th className="align-left">Grade</th>
            <th className="center-content">Lock grade</th>
            <th className="center-content">Hide grade</th>
            <th className="center-content">Actions</th>
          </tr>
          </thead>
          <tbody>
          {getSortedPlayers().map(playerId => getRowPerStudent(playerId))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

StudentList.propTypes = {
  yourX: PropTypes.shape({
    plural: PropTypes.string.isRequired,
    singular: PropTypes.string.isRequired
  }).isRequired,
  players: PropTypes.instanceOf(Object).isRequired,
  lengthOfPlayers: PropTypes.number.isRequired,
  maxPlayers: PropTypes.number.isRequired,
  selectedChildren: PropTypes.instanceOf(Object).isRequired,
  lengthOfSelected: PropTypes.number.isRequired,
  addStudentRedirectError: PropTypes.bool.isRequired,
  onPrintCodesClick: PropTypes.func.isRequired,
  openEditChildModal: PropTypes.func.isRequired,
  openDeleteChildModal: PropTypes.func.isRequired,
  openBulkEditChildModal: PropTypes.func.isRequired,
  openBulkDeleteChildModal: PropTypes.func.isRequired,
  toggleSelected: PropTypes.func.isRequired,
  toggleLockHideGrade: PropTypes.func.isRequired,
  toggleSelectHeader: PropTypes.func.isRequired
};

export default StudentList;
